// mow-frontend/src/components/notifications/NewsFeedManager.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, FormControl, FormLabel, Input, Select, Textarea,
  useToast, VStack, Table, Thead, Tbody, Tr, Th, Td, IconButton,
  Text, Spinner, Alert, AlertIcon, HStack, Badge, useColorModeValue,
  Card, CardBody, CardHeader, Heading, Flex, Icon, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, SimpleGrid,
  Stat, StatLabel, StatNumber, StatHelpText, Progress, InputGroup,
  InputLeftElement, Menu, MenuButton, MenuList, MenuItem, Portal,
  Switch, Divider, Tag, TagLabel, Wrap, WrapItem, Skeleton,
  SkeletonText, Avatar, AvatarGroup, NumberInput, NumberInputField,
  Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, Checkbox, Stack
} from '@chakra-ui/react';
import {
  MdArchive, MdAdd, MdEdit, MdDelete, MdRefresh, MdSearch,
  MdFilterList, MdVisibility, MdVisibilityOff, MdStar,
  MdStarBorder, MdNotifications, MdWarning, MdInfo, MdError,
  MdCheckCircle, MdMoreVert, MdPublish, MdSchedule, MdTimer,
  MdAttachFile, MdLink, MdImage, MdTrendingUp, MdSort,
  MdCalendarToday, MdPeople
} from 'react-icons/md';
import { createPublicAlert, getActivePublicAlerts, archivePublicAlert, updatePublicAlert } from '../../services/publicAlertService';
import { formatDate } from '../../utils/date';
import { format, formatDistanceToNow } from 'date-fns';

const ALERT_TYPES = [
  { value: 'info', label: 'Info', color: 'blue', icon: MdInfo },
  { value: 'warning', label: 'Warning', color: 'yellow', icon: MdWarning },
  { value: 'alert', label: 'Alert', color: 'red', icon: MdError },
  { value: 'maintenance', label: 'Maintenance', color: 'orange', icon: MdWarning },
  { value: 'announcement', label: 'Announcement', color: 'purple', icon: MdNotifications }
];

function NewsFeedManager() {
  const [alerts, setAlerts] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState('info');
  const [priority, setPriority] = useState('normal');
  const [expiryDate, setExpiryDate] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [editingAlert, setEditingAlert] = useState(null);
  const [stats, setStats] = useState(null);
  const [isPinned, setIsPinned] = useState(false);
  
  const toast = useToast();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  
  const cardBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const accentColor = useColorModeValue("blue.500", "blue.300");

  useEffect(() => {
    fetchAlerts();
    calculateStats();
  }, []);

  const fetchAlerts = useCallback(async () => {
    setIsFetching(true);
    try {
      const data = await getActivePublicAlerts();
      setAlerts(data);
      calculateStats(data);
    } catch (error) {
      toast({ 
        title: "Error fetching alerts", 
        description: error.message, 
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
    } finally {
      setIsFetching(false);
    }
  }, [toast]);

  const calculateStats = (alertsData = alerts) => {
    const stats = {
      total: alertsData.length,
      byType: {},
      active: alertsData.filter(a => !a.archived).length,
      pinned: alertsData.filter(a => a.isPinned).length
    };

    ALERT_TYPES.forEach(type => {
      stats.byType[type.value] = alertsData.filter(a => a.type === type.value).length;
    });

    setStats(stats);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !message) {
      toast({
        title: "Missing fields",
        description: "Title and message are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      await createPublicAlert({ 
        title, 
        message, 
        type, 
        priority,
        expiryDate: expiryDate || null,
        targetAudience,
        isPinned,
        attachments
      });
      
      toast({ 
        title: "News Item Created", 
        description: "The new item is now live in the public app.", 
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
      
      setTitle('');
      setMessage('');
      setType('info');
      setPriority('normal');
      setExpiryDate('');
      setTargetAudience('all');
      setIsPinned(false);
      setAttachments([]);
      
      fetchAlerts();
    } catch (error) {
      toast({ 
        title: "Error Creating Item", 
        description: error.message, 
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingAlert) return;

    setIsLoading(true);
    try {
      await updatePublicAlert(editingAlert.id, editingAlert);
      
      toast({ 
        title: "Item Updated", 
        description: "The news item has been updated successfully.", 
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      
      onEditClose();
      setEditingAlert(null);
      fetchAlerts();
    } catch (error) {
      toast({ 
        title: "Error Updating Item", 
        description: error.message, 
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async (id) => {
    try {
      await archivePublicAlert(id);
      
      toast({ 
        title: "Item Archived", 
        description: "This item has been removed from the public feed.", 
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      
      fetchAlerts();
      onDeleteClose();
    } catch (error) {
      toast({ 
        title: "Error Archiving Item", 
        description: error.message, 
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
    }
  };

  const getTypeIcon = (alertType) => {
    const type = ALERT_TYPES.find(t => t.value === alertType);
    return type ? type.icon : MdInfo;
  };

  const getTypeColor = (alertType) => {
    const type = ALERT_TYPES.find(t => t.value === alertType);
    return type ? type.color : 'gray';
  };

  const filteredAndSortedAlerts = useMemo(() => {
    let filtered = alerts;

    if (searchTerm) {
      filtered = filtered.filter(alert =>
        alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(alert => alert.type === filterType);
    }

    const sorted = [...filtered].sort((a, b) => {
      switch(sortBy) {
        case 'newest':
          return new Date(b.timestamp) - new Date(a.timestamp);
        case 'oldest':
          return new Date(a.timestamp) - new Date(b.timestamp);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'type':
          return a.type.localeCompare(b.type);
        default:
          return 0;
      }
    });

    return sorted;
  }, [alerts, searchTerm, filterType, sortBy]);

  return (
    <VStack spacing={6} align="stretch">
      {stats && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm">Total Items</StatLabel>
                <StatNumber>{stats.total}</StatNumber>
                <StatHelpText>All time</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm">Active</StatLabel>
                <StatNumber color="green.500">{stats.active}</StatNumber>
                <StatHelpText>Currently live</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm">Pinned</StatLabel>
                <StatNumber color="blue.500">{stats.pinned}</StatNumber>
                <StatHelpText>Priority items</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm">This Week</StatLabel>
                <StatNumber>{alerts.filter(a => {
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return new Date(a.timestamp) > weekAgo;
                }).length}</StatNumber>
                <StatHelpText>Recent activity</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      <Card bg={cardBg} borderRadius="xl">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">
              <Icon as={MdAdd} mr={2} />
              Create New News/Alert Item
            </Heading>
          </Flex>
        </CardHeader>
        
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">Title</FormLabel>
                  <Input 
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)} 
                    placeholder="e.g., Scheduled Water Main Upgrade"
                    size="lg"
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">Type</FormLabel>
                  <Select 
                    value={type} 
                    onChange={(e) => setType(e.target.value)}
                    size="lg"
                  >
                    {ALERT_TYPES.map(alertType => (
                      <option key={alertType.value} value={alertType.value}>
                        {alertType.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">Message</FormLabel>
                <Textarea 
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)} 
                  placeholder="Provide details about the event..."
                  rows={4}
                  size="lg"
                />
                <Text fontSize="xs" color={mutedColor} mt={1}>
                  {message.length} characters
                </Text>
              </FormControl>

              <Accordion allowToggle>
                <AccordionItem border="none">
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="600">
                      Advanced Options
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <FormControl>
                          <FormLabel fontSize="sm">Priority</FormLabel>
                          <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Target Audience</FormLabel>
                          <Select value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)}>
                            <option value="all">All Users</option>
                            <option value="residents">Residents Only</option>
                            <option value="business">Business Only</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Expiry Date</FormLabel>
                          <Input
                            type="datetime-local"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                          />
                        </FormControl>
                      </SimpleGrid>

                      <FormControl display="flex" alignItems="center">
                        <FormLabel htmlFor="pin-item" mb="0" fontSize="sm">
                          Pin to top of feed
                        </FormLabel>
                        <Switch
                          id="pin-item"
                          isChecked={isPinned}
                          onChange={(e) => setIsPinned(e.target.checked)}
                        />
                      </FormControl>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>

              <Divider />

              <HStack justify="space-between">
                <Button
                  variant="outline"
                  leftIcon={<MdVisibility />}
                  onClick={onPreviewOpen}
                  isDisabled={!title || !message}
                >
                  Preview
                </Button>

                <HStack>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setTitle('');
                      setMessage('');
                      setType('info');
                      setPriority('normal');
                    }}
                  >
                    Clear
                  </Button>
                  
                  <Button 
                    type="submit" 
                    colorScheme="green" 
                    isLoading={isLoading}
                    leftIcon={<MdPublish />}
                    size="lg"
                  >
                    Post to News Feed
                  </Button>
                </HStack>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>

      <Card bg={cardBg} borderRadius="xl">
        <CardHeader>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <Heading size="md">Active News & Alerts</Heading>
            
            <HStack spacing={2}>
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <Icon as={MdSearch} color={mutedColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Menu>
                <MenuButton as={IconButton} icon={<MdFilterList />} variant="ghost" />
                <Portal>
                  <MenuList>
                    <MenuItem onClick={() => setFilterType('all')}>All Types</MenuItem>
                    {ALERT_TYPES.map(type => (
                      <MenuItem key={type.value} onClick={() => setFilterType(type.value)}>
                        <Icon as={type.icon} mr={2} />
                        {type.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Portal>
              </Menu>

              <Menu>
                <MenuButton as={IconButton} icon={<MdSort />} variant="ghost" />
                <Portal>
                  <MenuList>
                    <MenuItem onClick={() => setSortBy('newest')}>Newest First</MenuItem>
                    <MenuItem onClick={() => setSortBy('oldest')}>Oldest First</MenuItem>
                    <MenuItem onClick={() => setSortBy('title')}>Title (A-Z)</MenuItem>
                    <MenuItem onClick={() => setSortBy('type')}>Type</MenuItem>
                  </MenuList>
                </Portal>
              </Menu>

              <Tooltip label="Refresh">
                <IconButton
                  icon={<MdRefresh />}
                  onClick={fetchAlerts}
                  variant="ghost"
                />
              </Tooltip>
            </HStack>
          </Flex>
        </CardHeader>
        
        <CardBody>
          {isFetching ? (
            <VStack spacing={3}>
              {[1, 2, 3].map(i => (
                <Box key={i} w="full">
                  <Skeleton height="60px" />
                </Box>
              ))}
            </VStack>
          ) : filteredAndSortedAlerts.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              {searchTerm || filterType !== 'all' 
                ? 'No items match your filters.' 
                : 'There are no active items in the news feed.'}
            </Alert>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Title</Th>
                  <Th>Type</Th>
                  <Th>Priority</Th>
                  <Th>Last Updated</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredAndSortedAlerts.map(alert => (
                  <Tr 
                    key={alert.id}
                    _hover={{ bg: hoverBg }}
                  >
                    <Td>
                      <VStack align="start" spacing={0}>
                        <HStack>
                          {alert.isPinned && (
                            <Tooltip label="Pinned">
                              <Icon as={MdStar} color="yellow.500" />
                            </Tooltip>
                          )}
                          <Text fontWeight="600">{alert.title}</Text>
                        </HStack>
                        <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                          {alert.message}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={getTypeColor(alert.type)}>
                        <HStack spacing={1}>
                          <Icon as={getTypeIcon(alert.type)} />
                          <span>{alert.type}</span>
                        </HStack>
                      </Badge>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={
                          alert.priority === 'critical' ? 'red' :
                          alert.priority === 'high' ? 'orange' :
                          alert.priority === 'normal' ? 'blue' : 'gray'
                        }
                      >
                        {alert.priority || 'normal'}
                      </Badge>
                    </Td>
                    <Td>
                      <Tooltip label={format(new Date(alert.timestamp), 'PPpp')}>
                        <Text fontSize="sm">
                          {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td>
                      <Badge colorScheme="green">
                        <Icon as={MdCheckCircle} mr={1} />
                        Active
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="Edit">
                          <IconButton
                            icon={<MdEdit />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingAlert(alert);
                              onEditOpen();
                            }}
                          />
                        </Tooltip>
                        
                        <Tooltip label="Archive">
                          <IconButton
                            icon={<MdArchive />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => {
                              setSelectedAlert(alert);
                              onDeleteOpen();
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit News Item</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editingAlert && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={editingAlert.title}
                    onChange={(e) => setEditingAlert({ ...editingAlert, title: e.target.value })}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    value={editingAlert.message}
                    onChange={(e) => setEditingAlert({ ...editingAlert, message: e.target.value })}
                    rows={4}
                  />
                </FormControl>
                
                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>Type</FormLabel>
                    <Select
                      value={editingAlert.type}
                      onChange={(e) => setEditingAlert({ ...editingAlert, type: e.target.value })}
                    >
                      {ALERT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Priority</FormLabel>
                    <Select
                      value={editingAlert.priority || 'normal'}
                      onChange={(e) => setEditingAlert({ ...editingAlert, priority: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleEdit} isLoading={isLoading}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Archive News Item</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              This action will remove the item from the public feed.
            </Alert>
            <Text>
              Are you sure you want to archive "{selectedAlert?.title}"?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={() => selectedAlert && handleArchive(selectedAlert.id)}
            >
              Archive
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Card bg={hoverBg}>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <HStack>
                    <Icon as={getTypeIcon(type)} color={`${getTypeColor(type)}.500`} />
                    <Badge colorScheme={getTypeColor(type)}>{type}</Badge>
                    {isPinned && <Badge colorScheme="yellow">Pinned</Badge>}
                  </HStack>
                  
                  <Heading size="md">{title || 'Notification Title'}</Heading>
                  <Text>{message || 'Notification message will appear here...'}</Text>
                  
                  <Divider />
                  
                  <HStack fontSize="sm" color={mutedColor}>
                    <Icon as={MdCalendarToday} />
                    <Text>Just now</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPreviewClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default NewsFeedManager;